export type PaletteId = "mono" | "ember" | "ice" | "sepia" | "aurora";

export interface Palette {
  id: PaletteId;
  name: string;
  /** Gradient stops as sRGB hex; each particle samples one position along them. */
  stops: string[];
  /** Background tint (sRGB hex); scaled by the backgroundShade param. */
  bgTint: string;
}

/** Uniform arrays are fixed-size; stops are padded to this length on upload. */
export const MAX_STOPS = 6;

export const PALETTES: Record<PaletteId, Palette> = {
  mono: {
    id: "mono",
    name: "Mono",
    stops: ["#b9b9c2", "#f4f4f6", "#ffffff"],
    bgTint: "#0a0a0c",
  },
  ember: {
    id: "ember",
    name: "Ember",
    stops: ["#8c2f1b", "#e5602a", "#ff9d4d", "#ffd9a0"],
    bgTint: "#0d0705",
  },
  ice: {
    id: "ice",
    name: "Ice",
    stops: ["#2f5f8f", "#5f9fd4", "#a8d4f0", "#eaf6ff"],
    bgTint: "#05080d",
  },
  sepia: {
    id: "sepia",
    name: "Sepia",
    stops: ["#6f5b41", "#a68a64", "#d4b896", "#f0e0c8"],
    bgTint: "#0c0a06",
  },
  aurora: {
    id: "aurora",
    name: "Aurora",
    stops: ["#7a5fd4", "#3fb8d4", "#6fe8b0", "#c8ffe0"],
    bgTint: "#050a0a",
  },
};

export const PALETTE_IDS = Object.keys(PALETTES) as PaletteId[];

/** sRGB hex → linear-light RGB triple (trail buffer accumulates in linear). */
export function hexToLinear(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  const srgb = [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff].map((c) => c / 255);
  const lin = srgb.map((c) =>
    c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4),
  );
  return [lin[0]!, lin[1]!, lin[2]!];
}

/**
 * Flattened vec3 stop array padded to MAX_STOPS by repeating the last stop,
 * ready for a `uniform vec3 u_stops[MAX_STOPS]` upload. Also returns the
 * real stop count for interpolation.
 */
export function paletteStopsLinear(p: Palette): {
  stops: Float32Array;
  count: number;
} {
  const out = new Float32Array(MAX_STOPS * 3);
  for (let i = 0; i < MAX_STOPS; i++) {
    const hex = p.stops[Math.min(i, p.stops.length - 1)]!;
    const [r, g, b] = hexToLinear(hex);
    out[i * 3] = r;
    out[i * 3 + 1] = g;
    out[i * 3 + 2] = b;
  }
  return { stops: out, count: p.stops.length };
}
